import {
  BillingAddress,
  DEFAULT_BILLING_COUNTRY,
  DEFAULT_PLATFORM_IRPF_PERCENT,
  DEFAULT_PLATFORM_VAT_RATE_PERCENT,
  PlatformIssuerType,
  PlatformBillingSettings,
} from '../../models/billing.model';
import { isValidCif, isValidDniNie, normalizeTaxId } from '../validation/spanish-tax-id';

function parseAddress(raw: unknown): BillingAddress | null {
  if (!raw || typeof raw !== 'object') return null;
  const a = raw as Record<string, unknown>;
  const line1 = typeof a['line1'] === 'string' ? a['line1'].trim() : '';
  const postalCode = typeof a['postalCode'] === 'string' ? a['postalCode'].trim() : '';
  const city = typeof a['city'] === 'string' ? a['city'].trim() : '';
  const province = typeof a['province'] === 'string' ? a['province'].trim() : '';
  if (a['country'] !== DEFAULT_BILLING_COUNTRY) return null;
  if (!line1 || !postalCode || !city || !province) return null;
  return { line1, postalCode, city, province, country: DEFAULT_BILLING_COUNTRY };
}

export function parsePlatformBilling(raw: unknown): PlatformBillingSettings | null {
  if (!raw || typeof raw !== 'object') return null;
  const pb = raw as Record<string, unknown>;
  const legalName = typeof pb['legalName'] === 'string' ? pb['legalName'].trim() : '';
  const taxId = typeof pb['taxId'] === 'string' ? normalizeTaxId(pb['taxId']) : '';
  const address = parseAddress(pb['address']);
  const issuerType: PlatformIssuerType = pb['issuerType'] === 'company'
    || pb['issuerType'] === 'self_employed'
    ? pb['issuerType']
    : isValidCif(taxId) ? 'company' : 'self_employed';
  const vatRatePercent = typeof pb['vatRatePercent'] === 'number' && Number.isFinite(pb['vatRatePercent'])
    ? pb['vatRatePercent']
    : null;
  const irpfWithholdingPercent = typeof pb['irpfWithholdingPercent'] === 'number'
    && Number.isFinite(pb['irpfWithholdingPercent'])
    ? pb['irpfWithholdingPercent']
    : null;
  const withholdingEnabled = typeof pb['withholdingEnabled'] === 'boolean'
    ? pb['withholdingEnabled']
    : issuerType === 'self_employed' && Number(irpfWithholdingPercent ?? 0) > 0;
  if (!legalName || !taxId || !address || vatRatePercent === null || irpfWithholdingPercent === null) {
    return null;
  }
  const updatedAt = typeof pb['updatedAt'] === 'number' && Number.isFinite(pb['updatedAt'])
    ? pb['updatedAt']
    : undefined;
  return {
    legalName,
    taxId,
    address,
    issuerType,
    withholdingEnabled,
    vatRatePercent,
    irpfWithholdingPercent,
    updatedAt,
  };
}

export function isValidPlatformTaxId(taxId: string): boolean {
  const v = normalizeTaxId(taxId);
  return isValidCif(v) || isValidDniNie(v);
}

export function isValidPlatformTaxIdForIssuer(
  taxId: string,
  issuerType: PlatformIssuerType,
): boolean {
  const v = normalizeTaxId(taxId);
  return issuerType === 'company' ? isValidCif(v) : isValidDniNie(v);
}

export function isPlatformBillingComplete(settings: PlatformBillingSettings | null | undefined): boolean {
  if (!settings) return false;
  if (settings.legalName.trim().length < 3) return false;
  if (!isValidPlatformTaxIdForIssuer(settings.taxId, settings.issuerType)) return false;
  const a = settings.address;
  if (!a.line1.trim() || !a.city.trim() || !a.province.trim()) return false;
  if (!/^\d{5}$/.test(a.postalCode.trim())) return false;
  if (settings.vatRatePercent < 0 || settings.vatRatePercent > 100) return false;
  if (settings.irpfWithholdingPercent < 0 || settings.irpfWithholdingPercent > 47) return false;
  return a.country === DEFAULT_BILLING_COUNTRY;
}

export function emptyPlatformBilling(): PlatformBillingSettings {
  return {
    legalName: '',
    taxId: '',
    issuerType: 'self_employed',
    withholdingEnabled: true,
    address: {
      line1: '',
      postalCode: '',
      city: 'Madrid',
      province: 'Madrid',
      country: DEFAULT_BILLING_COUNTRY,
    },
    vatRatePercent: DEFAULT_PLATFORM_VAT_RATE_PERCENT,
    irpfWithholdingPercent: DEFAULT_PLATFORM_IRPF_PERCENT,
  };
}
